import { type Session, sessionSchema } from '@rosetta/shared';
import { ecrireJson, lireJson } from './stockage';

export const CLE_SESSION = 'rosetta.session';

/**
 * Fallback lifetime (24 h), used only when Tahiti does not say how long the token
 * lasts. The application never imposes a lifetime of its own: it comes from the
 * `expiresIn` of the login answer.
 */
export const DUREE_SESSION_DEFAUT_MS = 24 * 60 * 60 * 1000;

/**
 * The `expireLe` of a session received at `maintenant`: the `expiresIn` returned by
 * Tahiti at login (in seconds), or 24 h when the answer gives none.
 */
export function expireLeApres(expiresInSecondes?: number, maintenant: number = Date.now()): string {
  const dureeMs =
    expiresInSecondes === undefined ? DUREE_SESSION_DEFAUT_MS : expiresInSecondes * 1000;
  return new Date(maintenant + dureeMs).toISOString();
}

/**
 * Keep the session for the tab only (`sessionStorage`), or across restarts of the
 * browser (`localStorage`) when the user ticked "se souvenir de moi". The other
 * storage is emptied so the session never lives in both.
 *
 * Known risk, documented in the spec: a token in `localStorage` is readable by any
 * script of the page.
 */
export function enregistrerSession(session: Session, seSouvenir = false): void {
  const [cible, autre] = seSouvenir
    ? [window.localStorage, window.sessionStorage]
    : [window.sessionStorage, window.localStorage];
  autre.removeItem(CLE_SESSION);
  ecrireJson(cible, CLE_SESSION, session);
}

/**
 * Is a session stored that has expired (#13)? Read BEFORE `lireSession`, which forgets an expired
 * session: the guard tells "your session expired" apart from "you never signed in".
 */
export function sessionExpiree(maintenant: number = Date.now()): boolean {
  return [window.sessionStorage, window.localStorage].some((storage) => {
    const session = lireJson(storage, CLE_SESSION, sessionSchema);
    return session !== null && Date.parse(session.expireLe) <= maintenant;
  });
}

/** The stored session, or `null` when there is none, it is invalid or it has expired. */
export function lireSession(maintenant: number = Date.now()): Session | null {
  for (const storage of [window.sessionStorage, window.localStorage]) {
    const session = lireJson(storage, CLE_SESSION, sessionSchema);
    if (session === null) continue;
    if (Date.parse(session.expireLe) <= maintenant) {
      storage.removeItem(CLE_SESSION);
      continue;
    }
    return session;
  }
  return null;
}

export function effacerSession(): void {
  window.sessionStorage.removeItem(CLE_SESSION);
  window.localStorage.removeItem(CLE_SESSION);
}
